import { Injectable, signal } from '@angular/core';

/** Visual treatment for a toast (amber left-border + optional icon). */
export type ToastVariant = 'success' | 'error' | 'info';

/** A single, currently-visible toast notification. */
export interface ToastI {
  /** Stable identity used for tracking and dismissal. */
  id: number;
  /** Body copy shown to the user. */
  message: string;
  /** Treatment driving the icon + accent (`info` by default). */
  variant: ToastVariant;
  /** Optional Boxicon class suffix (e.g. `bx-check`). Falls back to the variant icon. */
  icon?: string;
}

/** How long a toast stays on screen before auto-dismissing (ms). */
export const TOAST_DURATION_MS = 3200;

/** Most toasts rendered at once — oldest is evicted (Shell.jsx behaviour). */
export const TOAST_MAX_VISIBLE = 3;

/**
 * ToastService — signal-based, app-wide toast queue.
 *
 * Mirrors the `ToastContainer` from `ui_kits/customer/portal/Shell.jsx`:
 * fire-and-forget notifications that auto-dismiss after ~3.2s. Consumers call
 * `success` / `error` / `info` (or the generic `show`); the
 * `ToastContainerComponent` renders `toasts()` reactively.
 *
 * Provided in root so any portal can inject it without re-providing.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<ToastI[]>([]);
  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** Read-only view of the active toast stack (oldest first). */
  readonly toasts = this._toasts.asReadonly();

  /** Show a success toast (amber check icon by default). */
  success(message: string, icon?: string): number {
    return this.show(message, 'success', icon);
  }

  /** Show an error toast. */
  error(message: string, icon?: string): number {
    return this.show(message, 'error', icon);
  }

  /** Show an informational toast. */
  info(message: string, icon?: string): number {
    return this.show(message, 'info', icon);
  }

  /**
   * Enqueue a toast and schedule auto-dismissal. Returns the toast id so
   * callers may dismiss it early if needed.
   */
  show(message: string, variant: ToastVariant = 'info', icon?: string): number {
    const id = this.nextId++;
    const toast: ToastI = { id, message, variant, icon };

    this._toasts.update(current => {
      const next = [...current, toast];
      // Evict the oldest while over the cap, clearing its timer to avoid leaks.
      while (next.length > TOAST_MAX_VISIBLE) {
        const [removed] = next.splice(0, 1);
        this.clearTimer(removed.id);
      }
      return next;
    });

    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), TOAST_DURATION_MS),
    );

    return id;
  }

  /** Remove a toast immediately (used by the auto-dismiss timer and the UI). */
  dismiss(id: number): void {
    this.clearTimer(id);
    this._toasts.update(current => current.filter(toast => toast.id !== id));
  }

  /** Clear every visible toast and pending timer. */
  clear(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this._toasts.set([]);
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
